import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@pentabd/ui";

import {
  InputTextAreaRow,
  InputTextRow,
  MultiFile,
  SectionTitle,
  SelectControllerRow,
} from "@components";

import { useTicketDropdown } from "@hooks/tickets/ticket-settings/useTicketDropdown";
import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";

import { BASE_DATA } from "@constants/base-data";
import { URLS } from "@constants/urls";

import { getConvertedUnitData } from "@helpers/baseDataConvert";
import { ticketDropdownDataConverter } from "@helpers/tickets/ticketDropdownDataConverter";

import {
  CREATE_TICKET,
  CreateTicketFormDataType,
} from "@validations/Tickets/createTicketFormValidation";

const { TICKET_CATEGORIES, TICKET_TYPES, TICKET_PRIORITIES } = URLS;

export const CreateTicketForm = ({
  redirectToListPage,
}: {
  redirectToListPage: () => void;
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { data: wings } = useBaseData(BASE_DATA.WINGS);
  const ticketTypes = useTicketDropdown(TICKET_TYPES);
  const categories = useTicketDropdown(TICKET_CATEGORIES);
  const priorities = useTicketDropdown(TICKET_PRIORITIES);

  const {
    register,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<CreateTicketFormDataType>();

  return (
    <div>
      {/* ticket information  */}
      <SectionTitle label="TICKETS.CREATE.FORM.TICKET_INFO.LABEL" size="lg" />
      <div className="d-flex flex-column gap-8">
        <SelectControllerRow
          label="TICKETS.CREATE.FORM.TICKET_INFO.TYPE"
          name={CREATE_TICKET.TYPE}
          register={{ ...register(CREATE_TICKET.TYPE) }}
          options={ticketDropdownDataConverter(ticketTypes)}
          setValue={setValue}
          control={control}
          error={errors}
          required
        />
        <SelectControllerRow
          label="TICKETS.CREATE.FORM.TICKET_INFO.CATEGORY"
          name={CREATE_TICKET.CATEGORY}
          register={{ ...register(CREATE_TICKET.CATEGORY) }}
          options={ticketDropdownDataConverter(categories)}
          setValue={setValue}
          control={control}
          error={errors}
          required
        />
        <SelectControllerRow
          label="TICKETS.TICKET_LIST.FORM.SELECT.UNIT"
          name={CREATE_TICKET.UNIT}
          register={{ ...register(CREATE_TICKET.UNIT) }}
          options={getConvertedUnitData(wings, language)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          disabled
        />
        <InputTextRow
          register={{ ...register(CREATE_TICKET.SUBJECT) }}
          label="TICKETS.CREATE.FORM.TICKET_INFO.SUBJECT"
          error={errors as any}
          required
        />
        <InputTextRow
          register={{ ...register(CREATE_TICKET.ISSUE_NAME) }}
          label="TICKETS.CREATE.FORM.TICKET_INFO.ISSUE_NAME"
          error={errors as any}
        />
        <InputTextAreaRow
          register={{ ...register(CREATE_TICKET.ISSUE_DETAILS) }}
          label="TICKETS.CREATE.FORM.TICKET_INFO.ISSUE_DETAILS"
          error={errors as any}
          required
        />
        <SelectControllerRow
          label="TICKETS.CREATE.FORM.TICKET_INFO.PRIORITY"
          name={CREATE_TICKET.PRIORITY}
          register={{ ...register(CREATE_TICKET.PRIORITY) }}
          options={ticketDropdownDataConverter(priorities)}
          setValue={setValue}
          control={control}
          error={errors}
          required
        />
      </div>

      {/* document upload  */}
      <SectionTitle
        label="TICKETS.CREATE.FORM.SUPPORTING_DOCUMENTS.LABEL"
        size="lg"
      />

      <div className="row">
        <div className="col-lg-3">
          <Text weight="semibold" color="title" size="sm" component="p">
            {t("TICKETS.CREATE.FORM.SUPPORTING_DOCUMENTS.DOCUMENTS.LABEL")}
          </Text>
        </div>
        <div className="col-lg-7">
          <MultiFile
            id={CREATE_TICKET.DOCUMENTS}
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-8 border-top pt-10 mt-10">
        <Button
          size="sm"
          fill="outline"
          type="light"
          onClick={redirectToListPage}
        >
          {t("TICKETS.CREATE.FORM.CANCEL")}
        </Button>

        <Button size="sm" fill="fill" type="primary" htmlType="submit">
          {t("TICKETS.CREATE.FORM.SUBMIT")}
        </Button>
      </div>
    </div>
  );
};
